import { useOnBehalfOfAccountId } from '@openx/components-deal/lib/DealForm/hooks/useOnBehalfOfAccountId';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext';
import { useOrganizationsByIdsFetch } from '../../modules/deals/hooks/useOrganizationsByIdsFetch';
import { useGetContextualPricing } from '../targeting/utils/useGetContextualPricing';
import { useAmazonSyncStatusOptionsFetch } from './utils/amazonSync/useAmazonSyncStatusOptionsFetch';
import { useDealInsightsFilterOptions } from './utils/dealInsights/useDealInsightsFilterOptions';
import { useDealInsightsJobApi } from './utils/dealInsights/useDealInsightsJobApi';
import { useFetchDealInsights } from './utils/dealInsights/useFetchDealInsights';
import { useGetDealInsightsJobId } from './utils/dealInsights/useGetDealInsightsJobId';
import { useDealOperations } from './utils/dealOperations/useDealOperations';
import { useBuyersFetch } from './utils/participants/useBuyersFetch';
import { useDemandPartnersFetch } from './utils/participants/useDemandPartnersFetch';
import { useParticipantHooks } from './utils/participants/useParticipantHooks';
import { usePdpSyncDealStatusOptionsFetch } from './utils/pdpSync/usePdpSyncDealStatusOptionsFetch';
import { usePdpSyncNegotiationStatusOptionsFetch } from './utils/pdpSync/usePdpSyncNegotiationStatusOptionsFetch';
import { usePdpSyncStatusOptionsFetch } from './utils/pdpSync/usePdpSyncStatusOptionsFetch';
import { useDirectDealsPartnersFetch } from './utils/thirdPartyFees/useDirectDealsPartnersFetch';
import { useThirdPartyPartnersFetch } from './utils/thirdPartyFees/useThirdPartyPartnersFetch';
import {
  useDealTroubleshootingAgentMessageMutationState,
  useDealTroubleshootingAgentSendMessage,
  useDealTroubleshootingAgentSessionHistory,
  useDealTroubleshootingAgentSessionList,
} from './utils/troubleshootingAgent/useDealTroubleshootingAgent';
import { useDealTroubleshootingJobFetch } from './utils/troubleshootingFunnel/useDealTroubleshootingJobFetch';
import { useAccountOptionsFetch } from './utils/useAccountOptionsFetch';
import { useAgenciesOptionsFetch } from './utils/useAgenciesOptionsFetch';
import { useBrandsOptionsFetch } from './utils/useBrandsOptionsFetch';
import { useCanEditSegmentPartnerPlatformShare } from './utils/useCanEditSegmentPartnerPlatformShare';
import { useCurrencyFetch } from './utils/useCurrencyFetch';
import { useDealDiscounts } from './utils/useDealDiscounts';
import { useDealFetch } from './utils/useDealFetch';
import { useDealForecast } from './utils/useDealForecast';
import { useDealForecastActions } from './utils/useDealForecastActions';
import { useDealFormActions } from './utils/useDealFormActions';
import { useDealFormSelector } from './utils/useDealFormSelector';
import { useDealFormSubmit } from './utils/useDealFormSubmit';
import { useDealGroupFetch } from './utils/useDealGroupFetch';
import { useDealHealthScoreRootCauseDetails } from './utils/useDealHealthScoreRootCauseDetails';
import { useDealRboInfoFetch } from './utils/useDealRboInfoFetch';
import { useDealSourceFetch } from './utils/useDealSourceFetch';
import { useDealsFetch } from './utils/useDealsFetch';
import { useDealsLandingListFetch } from './utils/useDealsLandingListFetch';
import { useDealsListFetch } from './utils/useDealsListFetch';
import { useDealsRowActions } from './utils/useDealsRowActions';
import { useDealsTableFetch } from './utils/useDealsTableFetch';
import { useDealTroubleshootingFeedback } from './utils/useDealTroubleshootingFeedback';
import { useExpectedAdCategoryFetch } from './utils/useExpectedAdCategoryFetch';
import { useFetchDealForecast } from './utils/useFetchDealForecast';
import { useFetchDealIdGroupInfo } from './utils/useFetchDealIdGroupInfo';
import { useGetForecastJobId } from './utils/useGetForecastJobId';
import { useInitialValues, useInitialValuesLazy } from './utils/useInitialValues';
import { useMonetizingFetch } from './utils/useMonetizingFetch';
import { useMultipleDealsFetch } from './utils/useMultipleDealsFetch';
import { usePackageFetch, usePackageLazyFetch } from './utils/usePackageFetch';
import { useParticipantFunctions } from './utils/useParticipantFunctions';
import { useRefreshAuthToken } from './utils/useRefreshAuthToken';
import { useRelatedAccountsFetch } from './utils/useRelatedAccountsFetch';
import { useSetMultiproviderEnabledDataPartner } from './utils/useSetMultiproviderEnabledDataPartner';
import { useTroubleshootingFetch } from './utils/useTroubleshootingFetch';
import { useValidationSchema } from './utils/useValidationSchema';

const todoConfig = {
  useContentAttributesFetch: () => {
    throw new Error('useContentAttributesFetch not implemented.');
  },
  useCreativeTypesFetch: () => {
    throw new Error('useCreativeTypesFetch not implemented.');
  },
  useLanguagesFetch: () => {
    throw new Error('useLanguagesFetch not implemented.');
  },
  useMarketBrandGroupsFetch: () => {
    throw new Error('Function not implemented.');
  },
  useOnBehalfOfFetch: () => {
    throw new Error('useOnBehalfOfFetch not implemented.');
  },
  useOpenAuctionsAccessFetch: () => {
    throw new Error('useOpenAuctionsAccessFetch not implemented.');
  },
  usePostAuctionDiscountsFetch: () => {
    throw new Error('usePostAuctionDiscountsFetch not implemented.');
  },
  useFeePublisherPmpFetch: () => {
    throw new Error('useFeePublisherPmpFetch not implemented.');
  },
  useRebatesFetch: () => {
    throw new Error('useRebatesFetch not implemented.');
  },
  useDealRevenueBreakdownFetch: () => {
    throw new Error('useDealRevenueBreakdownFetch not implemented.');
  },
  useGlobalFeePublisherPmpFetch: () => {
    throw new Error('useGlobalFeePublisherPmpFetch not implemented.');
  },
  useTpfBoostedBidPriceFetch: () => {
    throw new Error('useTpfBoostedBidPriceFetch not implemented.');
  },
  useBuyerOrganizationsFetch: () => {
    throw new Error('useBuyerOrganizationsFetch not implemented.');
  },
} satisfies Partial<DealProviderConfig>;

export const dealConfig: DealProviderConfig = {
  ...todoConfig,
  isOa: true,
  useAccountOptionsFetch,
  useAgenciesOptionsFetch,
  useBrandsOptionsFetch,
  useBuyersFetch,
  useCurrenciesFetch: useCurrencyFetch,
  useCurrenciesFormFetch: () => ({
    currencies: useCurrencyFetch(),
  }),
  useDealFetch,
  useDealForecast,
  useDealForecastActions,
  useDealFormActions,
  useDealFormSelector,
  useDealGroupFetch,
  useDealHealthScoreRootCauseDetails,
  useDealOperations,
  useDealRboInfoFetch,
  useDealSourceFetch,
  useDealsFetch,
  useDealsLandingListFetch,
  useDealsListFetch,
  useDealsRowActions,
  useDealsTableFetch,
  useDemandPartnersFetch,
  useExpectedAdCategoryFetch,
  useFetchDealForecast,
  useFetchDealIdGroupInfo,
  useDealTroubleshootingJobFetch,
  useDealTroubleshootingFeedback,
  useDealTroubleshootingAgentSessionList,
  useDealTroubleshootingAgentSessionHistory,
  useDealTroubleshootingAgentSendMessage,
  useDealTroubleshootingAgentMessageMutationState,
  useRefreshAuthToken,
  useGetForecastJobId,
  useInitialValues,
  useInitialValuesLazy,
  useMonetizingFetch,
  useMultipleDealsFetch,
  usePackageFetch,
  usePackageLazyFetch,
  useParticipantFunctions,
  useParticipantHooks,
  useRelatedAccountsFetch,
  useThirdPartyPartnersFetch,
  useDirectDealsPartnersFetch,
  useTroubleshootingFetch,
  useValidationSchema,
  useDealInsightsFilterOptions,
  useDealInsightsJobApi,
  useGetDealInsightsJobId,
  useFetchDealInsights,
  useDealDiscounts,
  useCanEditSegmentPartnerPlatformShare,
  useSetMultiproviderEnabledDataPartner,
  useGetContextualPricing,
  useDealFormSubmit,
  useOrganizationsByIdsFetch,
  useOnBehalfOfAccountId,
  usePdpSyncDealStatusOptionsFetch,
  usePdpSyncNegotiationStatusOptionsFetch,
  usePdpSyncStatusOptionsFetch,
  useAmazonSyncStatusOptionsFetch,
};
